import type { Allocation, Route, Stop, TransportIncident, Trip, TripAttendance, Vehicle, VehicleMaintenance } from "../../services/transportService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * School transport's registers -- trips, riders, routes, the fleet,
 * maintenance and incidents -- and the rules the screens explain. Choices
 * mirror apps/transport/models.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const money = (value?: string | number | null) => (value === null || value === undefined || value === "" ? "" : `UGX ${Number(value).toLocaleString()}`);
const DAY_MS = 86_400_000;
export const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const VEHICLE_TYPES: [string, string][] = [["bus", "Bus"], ["minibus", "Minibus"], ["van", "Van"], ["car", "Car"], ["truck", "Truck"]];
export const FUEL_TYPES: [string, string][] = [["diesel", "Diesel"], ["petrol", "Petrol"], ["electric", "Electric"], ["hybrid", "Hybrid"]];
export const ROUTE_TYPES: [string, string][] = [["both", "Morning and evening"], ["pickup", "Morning pickup only"], ["dropoff", "Evening drop-off only"]];
export const PAYMENT_TYPES: [string, string][] = [["term", "Per term"], ["monthly", "Per month"], ["annual", "Per year"]];
export const MAINTENANCE_TYPES: [string, string][] = [["routine", "Routine service"], ["repair", "Repair"], ["inspection", "Inspection"], ["emergency", "Emergency"], ["upgrade", "Upgrade"]];
export const PRIORITIES: [string, string][] = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
export const INCIDENT_TYPES: [string, string][] = [
  ["breakdown", "Breakdown"],
  ["accident", "Accident"],
  ["delay", "Delay"],
  ["medical", "Medical emergency"],
  ["discipline", "Learner behaviour"],
  ["traffic", "Traffic"],
  ["weather", "Weather"],
  ["other", "Other"],
];
export const SEVERITIES: [string, string][] = [["minor", "Minor"], ["moderate", "Moderate"], ["major", "Major"], ["severe", "Severe"]];
/** What a driver or conductor marks on the boarding sheet. */
export const BOARDING: { value: string; label: string }[] = [
  { value: "picked_up", label: "On the bus" },
  { value: "dropped_off", label: "Dropped off" },
  { value: "absent", label: "Absent" },
];
export const BOARDING_LABELS: Record<string, string> = {
  picked_up: "On the bus",
  dropped_off: "Dropped off",
  absent: "Absent",
  present: "Present",
  late: "Late",
  excused: "Excused",
};

export const priorityDot = (priority: string) =>
  priority === "urgent" ? "bg-red-500" : priority === "high" ? "bg-amber-500" : priority === "medium" ? "bg-sky-500" : "bg-gray-400";
export const severityDot = (severity: string) =>
  severity === "severe" ? "bg-red-600" : severity === "major" ? "bg-red-500" : severity === "moderate" ? "bg-amber-500" : "bg-gray-400";

// --- Fees ------------------------------------------------------------------------------------

/** The route's catalogue fee for a billing period; mirrors TransportPaymentService's fallback (term = 3 months, year = 12). */
export function routeFee(route: Pick<Route, "monthly_fee" | "term_fee" | "annual_fee"> | undefined, paymentType: string): string {
  if (!route) return "";
  if (paymentType === "term") return route.term_fee || String(Number(route.monthly_fee) * 3);
  if (paymentType === "annual") return route.annual_fee || String(Number(route.monthly_fee) * 12);
  return route.monthly_fee;
}

// --- Trips -----------------------------------------------------------------------------------

export const isOpenTrip = (trip: Pick<Trip, "status">) => trip.status === "scheduled" || trip.status === "in_progress";

export function tripStatus(trip: Pick<Trip, "status" | "trip_date">, today: string) {
  switch (trip.status) {
    case "in_progress":
      return { label: "On the road", dot: "bg-sky-500" };
    case "completed":
      return { label: "Done", dot: "bg-emerald-500" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    case "delayed":
      return { label: "Delayed", dot: "bg-amber-500" };
    default:
      if (trip.trip_date < today) return { label: "Never left", dot: "bg-red-500" };
      if (trip.trip_date === today) return { label: "Due to leave", dot: "bg-amber-500" };
      return { label: "Scheduled", dot: "bg-indigo-500" };
  }
}

export const tripConfig = (today: string): RegisterConfig<Trip> => ({
  search: (row) => [row.route_name, row.vehicle_name, row.vehicle_registration, row.driver_name].join(" "),
  views: [
    { key: "today", label: "Today", match: (row) => row.trip_date === today },
    { key: "road", label: "On the road", match: (row) => row.status === "in_progress" },
    { key: "upcoming", label: "Coming up", match: (row) => row.status === "scheduled" && row.trip_date > today },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Trip date", value: (row) => row.trip_date },
  category: { label: "Route", value: (row) => row.route_name },
  sorts: [
    { key: "when", label: "Soonest first", compare: byText<Trip>((row) => `${row.trip_date}${row.scheduled_departure}`) },
    { key: "-when", label: "Latest first", compare: reversed(byText<Trip>((row) => `${row.trip_date}${row.scheduled_departure}`)) },
    { key: "route", label: "Route (A–Z)", compare: byText<Trip>((row) => row.route_name) },
  ],
});

/** Riders on a trip's route, in the order the bus reaches their pickup stop. */
export function tripRiders(trip: Pick<Trip, "route">, allocations: Allocation[], stops: Stop[]) {
  const order = new Map(stops.map((stop) => [stop.id, stop.sequence_order]));
  return allocations
    .filter((row) => row.route === trip.route && row.status === "active")
    .sort((a, b) => (order.get(a.pickup_stop) ?? 999) - (order.get(b.pickup_stop) ?? 999) || a.student_name.localeCompare(b.student_name));
}

/** What can be marked next for a rider, matching TripService.mark_student_attendance. */
export function boardingChoices(current?: string) {
  return BOARDING.map((choice) => ({
    ...choice,
    disabled:
      choice.value === current ||
      (choice.value === "dropped_off" && current !== "picked_up") ||
      (choice.value === "picked_up" && current === "dropped_off"),
  }));
}

export function boardingCounts(riders: Allocation[], marks: TripAttendance[]) {
  const byLearner = new Map(marks.map((mark) => [mark.student, mark.status]));
  const counts = { onBus: 0, droppedOff: 0, absent: 0, unmarked: 0 };
  for (const rider of riders) {
    const status = byLearner.get(rider.student);
    if (status === "picked_up") counts.onBus += 1;
    else if (status === "dropped_off") counts.droppedOff += 1;
    else if (status === "absent") counts.absent += 1;
    else counts.unmarked += 1;
  }
  return counts;
}

// --- Riders (allocations) -------------------------------------------------------------------

export function allocationStatus(row: Pick<Allocation, "status">) {
  switch (row.status) {
    case "pending":
      return { label: "Waiting for approval", dot: "bg-amber-500" };
    case "active":
      return { label: "Riding", dot: "bg-emerald-500" };
    case "suspended":
      return { label: "Suspended", dot: "bg-red-500" };
    case "completed":
      return { label: "Finished", dot: "bg-gray-400" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const allocationConfig: RegisterConfig<Allocation> = {
  search: (row) => [row.student_name, row.student_id, row.route_name, row.pickup_stop_name, row.dropoff_stop_name, row.emergency_contact_name].join(" "),
  views: [
    { key: "riding", label: "Riding", match: (row) => row.status === "active" },
    { key: "pending", label: "Waiting for approval", match: (row) => row.status === "pending" },
    { key: "suspended", label: "Suspended", match: (row) => row.status === "suspended" },
    { key: "ended", label: "Ended", match: (row) => row.status === "cancelled" || row.status === "completed" },
    { key: "all", label: "All" },
  ],
  date: { label: "Start date", value: (row) => row.start_date },
  category: { label: "Route", value: (row) => row.route_name },
  sorts: [
    { key: "learner", label: "Learner (A–Z)", compare: byText<Allocation>((row) => row.student_name) },
    { key: "route", label: "Route, then stop", compare: byText<Allocation>((row) => `${row.route_name} ${row.pickup_stop_name} ${row.student_name}`) },
    { key: "-start", label: "Newest first", compare: reversed(byText<Allocation>((row) => `${row.start_date}${String(row.id).padStart(8, "0")}`)) },
  ],
};

// --- Routes ----------------------------------------------------------------------------------

export function routeStatus(route: Pick<Route, "is_active" | "is_available" | "available_spots">) {
  if (!route.is_active) return { label: "Closed", dot: "bg-gray-400" };
  if (!route.is_available) return { label: "Not taking riders", dot: "bg-amber-500" };
  if (route.available_spots <= 0) return { label: "Full", dot: "bg-red-500" };
  return { label: `${route.available_spots} ${route.available_spots === 1 ? "place" : "places"} free`, dot: "bg-emerald-500" };
}

export const canTakeRiders = (route: Pick<Route, "is_active" | "is_available" | "available_spots">) => route.is_active && route.is_available && route.available_spots > 0;

export const routeConfig: RegisterConfig<Route> = {
  search: (row) => [row.name, row.start_location, row.end_location, row.description, ...row.stops.map((stop) => stop.name)].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "open", label: "Places free", match: canTakeRiders },
    { key: "full", label: "Full", match: (row) => row.is_active && row.available_spots <= 0 },
    { key: "closed", label: "Closed", match: (row) => !row.is_active || !row.is_available },
  ],
  category: { label: "Runs", value: (row) => labelFrom(ROUTE_TYPES, row.route_type) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<Route>((row) => row.name) },
    { key: "departure", label: "Leaves earliest", compare: byText<Route>((row) => row.departure_time) },
    { key: "-riders", label: "Most riders", compare: reversed(byNumber<Route>((row) => row.current_students)) },
  ],
};

/** The next free stop number, so a new stop goes on the end. */
export const nextStopNumber = (stops: Pick<Stop, "sequence_order">[]) => stops.reduce((highest, stop) => Math.max(highest, stop.sequence_order), 0) + 1;

// --- Fleet -----------------------------------------------------------------------------------

export const PAPERS_WARNING_DAYS = 30;

/** Insurance and the operating permit, each with how soon it runs out. */
export function papers(vehicle: Pick<Vehicle, "insurance_expiry" | "permit_expiry">, today: string) {
  return (
    [
      ["Insurance", vehicle.insurance_expiry],
      ["Permit", vehicle.permit_expiry],
    ] as const
  )
    .filter(([, expiry]) => expiry)
    .map(([label, expiry]) => {
      const days = daysBetween(today, expiry!);
      return { label, expiry: expiry!, days, expired: days < 0, soon: days >= 0 && days <= PAPERS_WARNING_DAYS };
    });
}

export const papersDue = (vehicle: Pick<Vehicle, "insurance_expiry" | "permit_expiry">, today: string) =>
  papers(vehicle, today).some((paper) => paper.expired || paper.soon);

export function vehicleStatus(vehicle: Pick<Vehicle, "status" | "is_active" | "insurance_expiry" | "permit_expiry" | "assigned_driver">, today: string) {
  if (!vehicle.is_active || vehicle.status === "retired" || vehicle.status === "sold") return { label: vehicle.status === "sold" ? "Sold" : "Off the fleet", dot: "bg-gray-400" };
  if (vehicle.status === "maintenance") return { label: "In the workshop", dot: "bg-amber-500" };
  if (papers(vehicle, today).some((paper) => paper.expired)) return { label: "Papers expired", dot: "bg-red-500" };
  if (!vehicle.assigned_driver) return { label: "No driver", dot: "bg-amber-500" };
  return { label: "On the road", dot: "bg-emerald-500" };
}

export const vehicleConfig = (today: string): RegisterConfig<Vehicle> => ({
  search: (row) => [row.name, row.registration_number, row.make, row.model, row.assigned_driver_name, row.parking_location].join(" "),
  views: [
    { key: "fleet", label: "In the fleet", match: (row) => row.is_active && row.status !== "retired" && row.status !== "sold" },
    { key: "workshop", label: "In the workshop", match: (row) => row.status === "maintenance" },
    { key: "papers", label: "Papers due", match: (row) => row.is_active && papersDue(row, today) },
    { key: "off", label: "Off the fleet", match: (row) => !row.is_active || row.status === "retired" || row.status === "sold" },
    { key: "all", label: "All" },
  ],
  category: { label: "Type", value: (row) => labelFrom(VEHICLE_TYPES, row.vehicle_type) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<Vehicle>((row) => row.name) },
    { key: "papers", label: "Papers expiring first", compare: byText<Vehicle>((row) => [row.insurance_expiry, row.permit_expiry].filter(Boolean).sort()[0] ?? "9999") },
    { key: "-seats", label: "Most seats", compare: reversed(byNumber<Vehicle>((row) => row.seating_capacity)) },
  ],
});

// --- Maintenance -----------------------------------------------------------------------------

export function maintenanceStatus(row: Pick<VehicleMaintenance, "status">) {
  switch (row.status) {
    case "pending":
      return { label: "Waiting", dot: "bg-amber-500" };
    case "in_progress":
      return { label: "In the workshop", dot: "bg-sky-500" };
    case "completed":
      return { label: "Done", dot: "bg-emerald-500" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const isOpenJob = (row: Pick<VehicleMaintenance, "status">) => row.status === "pending" || row.status === "in_progress";

export const maintenanceConfig: RegisterConfig<VehicleMaintenance> = {
  search: (row) => [row.vehicle_name, row.vehicle_registration, row.title, row.description, row.work_performed, row.requested_by_name].join(" "),
  views: [
    { key: "open", label: "Open", match: isOpenJob },
    { key: "waiting", label: "Waiting", match: (row) => row.status === "pending" },
    { key: "workshop", label: "In the workshop", match: (row) => row.status === "in_progress" },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Requested", value: (row) => row.requested_date },
  category: { label: "Vehicle", value: (row) => row.vehicle_name },
  sorts: [
    {
      key: "urgent",
      label: "Most urgent first",
      compare: (a, b) =>
        Number(isOpenJob(b)) - Number(isOpenJob(a)) ||
        ["urgent", "high", "medium", "low"].indexOf(a.priority) - ["urgent", "high", "medium", "low"].indexOf(b.priority) ||
        b.requested_date.localeCompare(a.requested_date),
    },
    { key: "-requested", label: "Newest first", compare: reversed(byText<VehicleMaintenance>((row) => `${row.requested_date}${String(row.id).padStart(8, "0")}`)) },
    { key: "vehicle", label: "Vehicle (A–Z)", compare: byText<VehicleMaintenance>((row) => row.vehicle_name) },
  ],
};

// --- Incidents -------------------------------------------------------------------------------

export function incidentStatus(row: Pick<TransportIncident, "is_resolved" | "investigation_required" | "investigation_completed">) {
  if (row.is_resolved) return { label: "Resolved", dot: "bg-emerald-500" };
  if (row.investigation_required && !row.investigation_completed) return { label: "To investigate", dot: "bg-red-500" };
  if (row.investigation_completed) return { label: "Investigated", dot: "bg-sky-500" };
  return { label: "Open", dot: "bg-amber-500" };
}

export const incidentConfig: RegisterConfig<TransportIncident> = {
  search: (row) => [row.title, row.description, row.location, row.route_name, row.vehicle_name, row.reported_by_name, ...(row.students_involved_names ?? [])].join(" "),
  views: [
    { key: "open", label: "Open", match: (row) => !row.is_resolved },
    { key: "investigate", label: "To investigate", match: (row) => !row.is_resolved && row.investigation_required && !row.investigation_completed },
    { key: "resolved", label: "Resolved", match: (row) => row.is_resolved },
    { key: "all", label: "All" },
  ],
  date: { label: "Incident date", value: (row) => row.incident_date },
  category: { label: "Kind", value: (row) => labelFrom(INCIDENT_TYPES, row.incident_type) },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<TransportIncident>((row) => `${row.incident_date}${row.incident_time}`)) },
    {
      key: "severity",
      label: "Most serious first",
      compare: (a, b) => SEVERITIES.findIndex(([key]) => key === b.severity) - SEVERITIES.findIndex(([key]) => key === a.severity),
    },
    { key: "route", label: "Route (A–Z)", compare: byText<TransportIncident>((row) => row.route_name) },
  ],
};
